import React, { useCallback, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useMutation, useApolloClient } from '@apollo/client';
import { useDocumentState } from '../../../../../../contexts/DocumentContext';
import classNames from 'classnames';
import { ReactComponent as CloseIcon } from '../../../../../../shared/icons/close-icon.svg';
import { prepareToSignDocument, saveDocumentVersion } from '../../../../../../api/mutations';
import { flushActiveEditor } from '../../../../document-editor/flushActiveEditor';
import { getDocumentById, getDocumentsByDeal } from '../../../../../../api/queries_v2';
import { useNotifications } from '../../../../../../notifications';
import { fillPlaceholders, paths } from '../../../../../../routing';
import { DocumentStatusEnum } from '../../../../../../shared/constants/document';
import { OptionalMessageInputRef } from '../../../../document-wizard-modal/custom-modals/document-wizard-action-modal/OptionalMessageInputRef';
import { DocumentSourceEnum } from '../../../../document-editor/constants/EditorConstats';
import { useDocumentRole } from '../../../../../../shared/hooks/useDocumentRole/useDocumentRole';
import styles from './SendToPreparingToSignInternalSlideout.module.scss';

interface SendToPreparingToSignInternalSlideoutProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: string;
  dealId: string;
  teamId: string;
  handleSaveDocument?: (() => Promise<void>) | undefined;
}

const SendToPreparingToSignInternalSlideout = ({
  isOpen,
  onClose,
  documentId,
  dealId,
  teamId,
  handleSaveDocument,
}: SendToPreparingToSignInternalSlideoutProps) => {
  const { state } = useDocumentState();
  const documentSource = state.source;
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const client = useApolloClient();
  const additionalMessageInputRef = useRef<HTMLTextAreaElement>(null);
  useDocumentRole(documentId);
  // Polymorphic version snapshot — the docx-native equivalent of CKEditor's
  // RevisionTracker.saveRevision().
  const [saveVersionMutation] = useMutation(saveDocumentVersion);
  const [sendToPreparingToSign] = useMutation(prepareToSignDocument, {
    refetchQueries: [
      {
        query: getDocumentById,
        variables: {
          documentId,
          confirm: true,
        },
      },
      {
        query: getDocumentsByDeal,
        variables: {
          dealId,
        },
      },
    ],
  });

  const [, { error: notifyError }] = useNotifications();

  const handleError = useCallback(() => {
    notifyError({
      message: 'Unable to send to preparing to sign.',
    });
  }, [notifyError]);

  const handleSendInternalMutation = useCallback(async () => {
    const isAppDocument = documentSource === DocumentSourceEnum.APP;

    if (isAppDocument) {
      handleSaveDocument && (await handleSaveDocument());

      // Land edits still inside the autosave debounce and reconcile the
      // suggestion rows the server gates on. Was a `window.editor` call that
      // silently no-oped under the DOCX-native editor.
      await flushActiveEditor(documentId);

      const currentDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
      const revisionName = `Prepared for Signing - ${currentDate}`;

      // Snapshot the document as prepared, via the polymorphic
      // saveDocumentVersion mutation.
      try {
        await saveVersionMutation({
          variables: {
            documentId: String(documentId),
            documentType: 'Document',
            name: revisionName,
            event: 'prepare_to_sign',
          },
        });
      } catch (error) {
        console.error('Failed to create prepare-for-signing revision:', error);
        // Don't block the operation if revision creation fails
      }
    }

    const additionalMessage = additionalMessageInputRef.current?.value;

    // Long enough for in-flight saves to land before the state transition reads the body.
    setTimeout(async () => {
      try {
        // Fetch document data with form fields and annotations
        const { data: documentData } = await client.query({
          query: getDocumentById,
          variables: { documentId },
          fetchPolicy: 'network-only', // Get fresh data
        });

        // PDF embedding happens at the final "Send for Signing" step, not here.
        // Preparing to sign displays readonly overlays from DB (pdfFormFields, pdfAnnotations)
        // This allows "Edit Document" to work correctly without re-embedding issues

        const mutationStartTime = Date.now();

        const { data } = await sendToPreparingToSign({
          variables: { documentId, additionalMessage },
          // Refetch document data but don't wait for PDF generation (now async)
          refetchQueries: [
            {
              query: getDocumentById,
              variables: { documentId },
            },
          ],
          // Removed awaitRefetchQueries: true to prevent hangs from async PDF generation
          awaitRefetchQueries: false,
        });

        const mutationTime = Date.now() - mutationStartTime;

        const preparedDocument = data?.prepareToSignDocument;

        if (!preparedDocument) {
          handleError();
          return;
        }

        if (preparedDocument.status !== DocumentStatusEnum.PREPARING_TO_SIGN) {
          handleError();
          return;
        }

        // PDF generation is now async, no need to wait for completion

        const signDocumentUrl = fillPlaceholders(paths.Documents.signDocument, {
          documentId,
          teamId,
          dealId,
        });

        // Navigate after everything is ready
        navigate(signDocumentUrl);
      } catch {
        handleError();
        setIsLoading(false);
      } finally {
        onClose();
      }
    }, 500); // Reduced from 2000ms since PDF generation is now async
  }, [
    client,
    dealId,
    documentId,
    handleError,
    onClose,
    sendToPreparingToSign,
    teamId,
    documentSource,
    handleSaveDocument,
    navigate,
  ]);

  const handleSend = useCallback(async () => {
    setIsLoading(true);
    return handleSendInternalMutation();
  }, [handleSendInternalMutation]);

  // Don't render anything if not showing
  if (!isOpen) {
    return null;
  }

  // Use React Portal to render slideout outside current DOM tree
  return ReactDOM.createPortal(
    <>
      {/* Dark overlay backdrop */}
      {isOpen && <div className={styles.slideoutBackdrop} />}

      <div
        className={classNames(
          styles.slideoutWrapper,
          isOpen ? styles.slideoutWrapper__show : styles.slideoutWrapper__collapsed
        )}
      >
        <div className={styles.slideout}>
          {/* Sticky Header */}
          <div className={styles.slideout__header}>
            <div className={styles.slideout__title}>
              <FormattedMessage
                id="document-wizard-modal.send-for-signing"
                defaultMessage="Prepare Document for Signing"
              />
            </div>
            <div className={styles.slideout__close} onClick={onClose}>
              <CloseIcon />
            </div>
          </div>

          {/* Scrollable Content */}
          <div className={styles.slideout__content}>
            <div className={styles.slideout__form}>
              {/* Description */}
              <div className={styles.slideout__description}>
                Lock this document, add signers and signature fields, and optionally include a message for all parties.
              </div>

              {/* Optional Message Input */}
              <div className={styles.slideout__messageSection}>
                <OptionalMessageInputRef ref={additionalMessageInputRef} />
              </div>
            </div>
          </div>

          {/* Footer with Actions */}
          <div className={styles.slideout__footer}>
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleSend} disabled={isLoading}>
              {isLoading && <span className="spinner-border spinner-border-sm me-2" />}
              Prepare for Signing
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default SendToPreparingToSignInternalSlideout;
